// Anmeldung im Admin-Frontend (Konzept Abschnitt 8 und 9):
// - normal: authentik, Authorization Code + PKCE (oidc-client-ts), public client
// - Notfall: Passkey direkt am Writer, wenn authentik nicht antwortet
//
// Tokens liegen nur im Speicher bzw. sessionStorage dieses Tabs, nie in
// localStorage.
import { startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

export type Session = { token: string; kind: 'oidc' | 'breakglass'; expiresAt: number };

export type Config = { oidc_issuer: string; client_id: string };

const BREAKGLASS_KEY = 'status-admin:breakglass';

export async function loadConfig(): Promise<Config> {
  const res = await fetch('/api/config');
  if (!res.ok) throw new Error('config not available');
  return (await res.json()) as Config;
}

export function createUserManager(config: Config): UserManager {
  return new UserManager({
    authority: config.oidc_issuer,
    client_id: config.client_id,
    redirect_uri: `${location.origin}/auth/callback`,
    post_logout_redirect_uri: `${location.origin}/`,
    response_type: 'code',
    scope: 'openid profile email status_roles',
    userStore: new WebStorageStateStore({ store: sessionStorage }),
    automaticSilentRenew: false,
  });
}

/** Antwortet authentik? Nur dann ist die Notfall-Anmeldung ausgeblendet. */
export async function oidcReachable(config: Config, timeoutMs = 4000): Promise<boolean> {
  try {
    const res = await fetch(new URL('.well-known/openid-configuration', config.oidc_issuer), {
      signal: AbortSignal.timeout(timeoutMs),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function breakglassLogin(): Promise<Session> {
  const start = await fetch('/api/breakglass/options', { method: 'POST' });
  if (!start.ok) throw new Error(((await start.json()) as { error?: string }).error ?? 'Notfall-Anmeldung nicht möglich');
  const { challenge_id, options } = (await start.json()) as { challenge_id: string; options: PublicKeyCredentialRequestOptionsJSON };
  const response = await startAuthentication({ optionsJSON: options });
  const res = await fetch('/api/breakglass/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge_id, response }),
  });
  const body = (await res.json()) as { token?: string; expires_at?: string; error?: string };
  if (!res.ok || !body.token || !body.expires_at) throw new Error(body.error ?? 'Notfall-Anmeldung fehlgeschlagen');
  const session: Session = { token: body.token, kind: 'breakglass', expiresAt: Date.parse(body.expires_at) };
  sessionStorage.setItem(BREAKGLASS_KEY, JSON.stringify(session));
  return session;
}

export function storedBreakglass(): Session | null {
  try {
    const s = JSON.parse(sessionStorage.getItem(BREAKGLASS_KEY) ?? 'null') as Session | null;
    return s && s.expiresAt > Date.now() ? s : null;
  } catch {
    return null;
  }
}

export function clearBreakglass() {
  sessionStorage.removeItem(BREAKGLASS_KEY);
}
